import React from 'react';
import styled from 'styled-components';

import PostCode from '@/components/PostCode';
import TextComponent from '@/components/TextComponent';
import FormInputComponent from '@/components/FormInputComponent';
import ButtonComponent from '@/components/ButtonComponent';
import BaseModal from '@/components/BaseModal';
import GlobalAlert from '@/components/GlobalAlert';
import { validateDetailAddress, validatePharmacyName, validateTel } from '@/utils/validate';
import { AcceptedFiles } from '../SignupPage';
import theme from '@/styles/theme';

interface Props {
  display: boolean;
  control: any;
  errors: any;
  setAddressForm: (data: AddressForm) => void;
  jibunAddress: string;
  roadAddress: string;
  postCodeModal: boolean;
  setPostCodeModal: (on: boolean) => void;
  addImageFile: (file: Array<File>, type: string) => void;
  fileName: any;
}

const PharmacyInfo = (props: Props) => {
  const {
    control,
    errors,
    setAddressForm,
    jibunAddress,
    roadAddress,
    postCodeModal,
    setPostCodeModal,
    addImageFile,
    fileName,
    display
  } = props;

  const fileAccept: AcceptedFiles = [
    'application/pdf',
    'image/jpg',
    'image/jpeg',
    'image/png'
  ];

  const hiddenFileInput = React.useRef<HTMLInputElement>(null);

  const handleClick = () => {
    hiddenFileInput?.current?.click();
  };

  const handleChange = (event: any) => {
    const fileUploaded: Array<File> = Array.from(event.target.files);

    if (fileUploaded[0].size > 10000000)
      return GlobalAlert.show({
        title: '파일 사이즈가 10MB 이하여야 합니다.'
      });

    fileUploaded.map((file: any) => {
      return Object.assign(file, {
        blob: URL.createObjectURL(file)
      });
    });
    addImageFile(fileUploaded, 'license');
  };

  return (
    <InfoSection display={display}>
      <Top>
        <OrangeBar />
        <InfoSectionTitle>약국 정보</InfoSectionTitle>
      </Top>
      <Body>
        <FormInputComponent
          keyName="pharmName"
          label={'약국명'}
          placeholder={'약국명을 입력해주세요'}
          defaultValue={''}
          control={control}
          error={errors.pharmName}
          rules={{
            required: '약국명을 입력해주세요',
            validate: validatePharmacyName
          }}
          maxLength={15}
        />
        <FormInputComponent
          keyName="roadAddress"
          label={'주소'}
          value={roadAddress || '주소를 검색해주세요'}
          disabled
          rightElement={
            <ButtonComponent
              bgColor={theme.DF_COLOR.GREEN500}
              borderColor={theme.DF_COLOR.GREEN500}
              textColor={theme.DF_COLOR.WHITE}
              fontWeight={600}
              fontSize={20}
              text={'주소찾기'}
              onClick={() => setPostCodeModal(true)}
            />
          }
          control={control}
          error={errors.roadAddress}
          rules={{
            minLength: { value: 1, message: '주소를 검색해주세요' }
          }}
        />
        <FormInputComponent
          keyName="jibunAddress"
          label={' '}
          value={jibunAddress || '주소를 검색해주세요'}
          disabled
          control={control}
          error={errors.jibunAddress}
          rules={{
            minLength: { value: 1, message: '주소를 검색해주세요' }
          }}
        />
        <FormInputComponent
          keyName="detailAddress"
          label={' '}
          placeholder={'상세주소를 입력해주세요'}
          defaultValue={''}
          control={control}
          error={errors.detailAddress}
          rules={{
            required: '상세주소를 입력해주세요',
            validate: validateDetailAddress
          }}
        />
        <FormInputComponent
          keyName="tel"
          label={'전화번호'}
          placeholder={'전화번호를 입력해주세요( - 없이, 숫자만 )'}
          defaultValue={''}
          control={control}
          error={errors.tel}
          rules={{
            required: '약국 전화번호를 입력해주세요',
            validate: validateTel
          }}
        />
        <FormInputComponent
          keyName="fax"
          label={'팩스번호'}
          placeholder={'팩스번호를 입력해주세요( - 없이, 숫자만 )'}
          defaultValue={''}
          control={control}
          error={errors.fax}
          rules={{
            required: '약국 팩스번호를 입력해주세요',
            validate: validateTel
          }}
        />
        <FormInputComponent
          keyName="licenseFileName"
          value={fileName.license || '파일을 선택해주세요'}
          disabled
          label={'약사면허증'}
          rightElement={
            <ButtonComponent
              bgColor={theme.DF_COLOR.GREEN500}
              borderColor={theme.DF_COLOR.GREEN500}
              textColor={theme.DF_COLOR.WHITE}
              fontWeight={600}
              fontSize={20}
              text={'파일선택'}
              onClick={() => handleClick()}
            />
          }
          control={control}
          rules={{
            minLength: { value: 1, message: '약사면허증을 확인해주세요' }
          }}
        />
        <input
          type="file"
          ref={hiddenFileInput}
          onChange={handleChange}
          style={{ display: 'none' }}
          accept={fileAccept.toString()}
        />
      </Body>
      {postCodeModal && (
        <BaseModal
          modalTitle={'주소 검색'}
          onClose={() => setPostCodeModal(false)}>
          <PostCode
            setAddressForm={setAddressForm}
            setPostCodeModal={setPostCodeModal}
          />
        </BaseModal>
      )}
    </InfoSection>
  );
};

export default PharmacyInfo;

const InfoSection = styled.div<{ display: boolean }>`
  display: ${({ display }) => (display ? 'block' : 'none')};
`;

const Top = styled.div`
  display: flex;
  margin-bottom: 45px;
`;
const OrangeBar = styled.div`
  background: #ff8d00;
  width: 4px;
  margin-right: 8px;
`;
const InfoSectionTitle = styled(TextComponent)`
  font-size: 20px;
  font-weight: 700;
`;
const Body = styled.div`
  padding-left: 10px;
`;
